import { useEffect, useState } from "react";
import { KeyRound, Loader2, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

const ENDPOINTS = [
  "/api/admin-api/v1/overview",
  "/api/admin-api/v1/orders",
  "/api/admin-api/v1/customers",
  "/api/admin-api/v1/esims",
  "/api/admin-api/v1/transactions",
];

/** Card "API amministratore": chiave personale admin (sima_) per agganciare sistemi
 *  esterni ai dati della piattaforma in sola lettura. Mostrata una sola volta. */
export default function AdminApiKey() {
  const { t, lang } = useLanguage();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [calls, setCalls] = useState(null);

  const load = () => {
    api.get("/admin/admin-api/key").then(({ data }) => setStatus(data.data)).catch(() => setStatus({ active: false }));
    api.get("/admin/admin-api/logs").then(({ data }) => setCalls(data.data || [])).catch(() => setCalls([]));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/admin-api/key");
      setNewKey(data.data.api_key);
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/admin-api/key");
      setNewKey(null);
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div data-testid="admin-api-card" className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#FF6B35]" /> {t("admapi.title")}
          </h3>
          <p className="text-sm text-slate-500 mt-1">{t("admapi.sub")}</p>
          {status.active && (
            <p data-testid="admapi-status" className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t("admapi.active")} · {status.prefix}
              {status.created_at && <span className="text-emerald-600/70 normal-case">· {new Date(status.created_at).toLocaleDateString(lang)}</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admapi-generate" onClick={generate} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-[#1E293B] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            {status.active ? t("admapi.rotate") : t("admapi.generate")}
          </button>
          {status.active && (
            <button data-testid="admapi-revoke" onClick={revoke} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:border-red-400 hover:text-red-500 transition-colors disabled:opacity-60">
              <Trash2 className="w-3.5 h-3.5" /> {t("admapi.revoke")}
            </button>
          )}
        </div>
      </div>

      {newKey && (
        <div data-testid="admapi-new-key" className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
          <div className="font-display font-bold text-emerald-800 text-sm mb-1">{t("admapi.keyTitle")}</div>
          <p className="text-xs text-emerald-700 mb-2">{t("dir.keyNote")}</p>
          <div className="rounded-xl bg-white border border-emerald-200 px-4 py-2.5 font-mono-t text-xs break-all select-all">{newKey}</div>
        </div>
      )}

      {status.active && (
        <div className="mt-4">
          <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("admapi.endpoints")}</div>
          <div className="flex flex-wrap gap-1.5" data-testid="admapi-endpoints">
            {ENDPOINTS.map((e) => (
              <span key={e} className="rounded-full bg-slate-50 border border-slate-100 px-3 py-1 font-mono-t text-[10px] text-slate-600">
                GET {e}
              </span>
            ))}
          </div>
        </div>
      )}

      {status.active && calls !== null && (
        <div className="mt-5">
          <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("admapi.calls")}</div>
          {calls.length === 0 ? (
            <p data-testid="admapi-calls-empty" className="text-xs text-slate-300">{t("admapi.callsEmpty")}</p>
          ) : (
            <div className="divide-y divide-slate-50" data-testid="admapi-calls">
              {calls.slice(0, 8).map((l) => (
                <div key={l.log_id} data-testid={`admapi-call-${l.log_id}`} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="font-mono-t text-[11px] text-slate-600 truncate">{l.method} {l.path}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className={`rounded-full px-2 py-0.5 font-mono-t text-[10px] font-bold ${l.status < 300 ? "bg-emerald-50 text-emerald-700" : l.status < 500 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>{l.status}</span>
                    <span className="font-mono-t text-[10px] text-slate-400">
                      {l.ms} ms · {new Date(l.created_at).toLocaleString(lang, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
